/**
 * FlowWizard - A beautiful, intentional step-by-step flow system
 *
 * This component orchestrates multi-step flows with:
 * - Smooth animations between steps
 * - Progress tracking
 * - Conditional step rendering
 * - Back/forward navigation
 * - Data persistence across steps
 */

import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants for step transitions
const stepVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
  }),
};

const FlowWizard = ({
  steps,
  initialData = {},
  onComplete,
  onCancel,
  title,
  subtitle,
  className = '',
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [flowData, setFlowData] = useState(initialData);
  const [direction, setDirection] = useState(0);
  const [stepHistory, setStepHistory] = useState([0]);

  // Filter steps based on conditions
  const visibleSteps = useMemo(() => {
    return steps.filter(step => {
      if (typeof step.condition === 'function') {
        return step.condition(flowData);
      }
      return step.condition !== false;
    });
  }, [steps, flowData]);

  const currentStep = visibleSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  // Calculate progress percentage
  const progress = ((currentStepIndex + 1) / visibleSteps.length) * 100;

  // Update flow data
  const updateData = useCallback((updates) => {
    setFlowData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Go to next step
  const goNext = useCallback((stepData = {}) => {
    // Use functional update to avoid stale data issues
    setFlowData(prev => {
      const newData = { ...prev, ...stepData };
      if (isLastStep) {
        onComplete?.(newData);
      }
      return newData;
    });

    if (!isLastStep) {
      setDirection(1);
      setCurrentStepIndex(prev => {
        const next = prev + 1;
        setStepHistory(h => [...h, next]);
        return next;
      });
    }
  }, [isLastStep, onComplete]);

  // Go to previous step
  const goBack = useCallback(() => {
    if (!isFirstStep) {
      setDirection(-1);
      setStepHistory(prev => {
        const newHistory = prev.slice(0, -1);
        setCurrentStepIndex(newHistory[newHistory.length - 1] || 0);
        return newHistory;
      });
    } else {
      onCancel?.();
    }
  }, [isFirstStep, onCancel]);

  // Jump to specific step (for non-linear flows)
  const goToStep = useCallback((stepId) => {
    const index = visibleSteps.findIndex(s => s.id === stepId);
    if (index !== -1) {
      setDirection(index > currentStepIndex ? 1 : -1);
      setCurrentStepIndex(index);
      setStepHistory(prev => [...prev, index]);
    }
  }, [visibleSteps, currentStepIndex]);

  // Context value for steps
  const flowContext = {
    data: flowData,
    updateData,
    goNext,
    goBack,
    goToStep,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    totalSteps: visibleSteps.length,
    progress,
  };

  if (!currentStep) {
    return null;
  }

  const StepComponent = currentStep.component;

  return (
    <div className={`flex flex-col h-full overflow-hidden bg-white dark:bg-[#0A0A0A] ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 px-8 pt-8 pb-6">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {visibleSteps.map((step, index) => (
            <motion.div
              key={step.id}
              className={`rounded-full transition-all duration-300 ${
                index <= currentStepIndex
                  ? 'bg-brand'
                  : 'bg-gray-200 dark:bg-[#2A2A2A]'
              }`}
              initial={false}
              animate={{
                width: index === currentStepIndex ? 24 : 8,
                height: 8,
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          ))}
        </div>

        {/* Title area */}
        {(title || currentStep.title) && (
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {currentStep.title || title}
            </h1>
            {(subtitle || currentStep.subtitle) && (
              <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
                {currentStep.subtitle || subtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Step content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep.id}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            className="px-8 pb-8"
          >
            <StepComponent {...flowContext} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FlowWizard;
